import {
  faCircleCheck,
  faInfoCircle,
  faTriangleExclamation,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useContext, useEffect, useRef } from "react";

import NotificationContext from "../context/NotificationContext";

import "./CustomAlerts.css";

const FOCUSABLE_SELECTOR = [
  "button:not([disabled])",
  "[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

function getFocusable(container) {
  if (!container) return [];
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute("disabled") && el.getAttribute("aria-hidden") !== "true",
  );
}

export default function CustomAlerts() {
  const ctx = useContext(NotificationContext);
  const dialogRef = useRef(null);
  const okButtonRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  /*
   * Auto-dismiss toast after 10 seconds when there is no confirm callback.
   */
  useEffect(() => {
    if (!ctx.showNotification || ctx.hasConfirm) {
      return;
    }

    const timer = setTimeout(() => {
      ctx.onClose();
    }, 10000);

    return () => {
      clearTimeout(timer);
    };
  }, [ctx]);

  /*
   * Confirm modal: focus OK, trap Tab, Escape to cancel, restore focus on close.
   */
  useEffect(() => {
    if (!ctx.showNotification || !ctx.hasConfirm) {
      return;
    }

    previouslyFocusedRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    const focusOk = () => {
      okButtonRef.current?.focus();
    };
    const frame = requestAnimationFrame(focusOk);

    const onKeyDown = (event) => {
      if (okButtonRef.current?.disabled) {
        event.preventDefault();
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        ctx.onClose();
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const focusable = getFocusable(dialogRef.current);
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey) {
        if (active === first || !dialogRef.current?.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last || !dialogRef.current?.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    };

    const onFocusIn = (event) => {
      if (!dialogRef.current) return;
      if (dialogRef.current.contains(event.target)) return;
      focusOk();
    };

    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("focusin", onFocusIn);

    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("focusin", onFocusIn);

      const prev = previouslyFocusedRef.current;
      previouslyFocusedRef.current = null;
      if (prev && typeof prev.focus === "function" && document.contains(prev)) {
        prev.focus();
      }
    };
  }, [ctx]);

  if (!ctx.showNotification) {
    return null;
  }

  const isError = ctx.status === "error";
  const isSuccess = ctx.status === "success";

  const statusClass = isError
    ? "notification-error"
    : isSuccess
      ? "notification-success"
      : "notification-info";

  const icon = isError
    ? faTriangleExclamation
    : isSuccess
      ? faCircleCheck
      : faInfoCircle;

  if (ctx.hasConfirm) {
    return (
      <div
        className="custom-alert-modal-backdrop"
        role="presentation"
        onMouseDown={(event) => {
          // Click on backdrop only — do not steal button clicks
          if (event.target === event.currentTarget && !ctx.confirming) {
            ctx.onClose();
          }
        }}
      >
        <div
          ref={dialogRef}
          className="custom-alert-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="custom-alert-modal-message"
        >
          <button
            type="button"
            className="custom-alert-modal-close"
            onClick={ctx.onClose}
            disabled={ctx.confirming}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>

          <div className={`custom-alert-icon-wrapper ${statusClass}`}>
            <FontAwesomeIcon icon={icon} />
          </div>

          <div className="custom-alert-modal-content">
            <div
              id="custom-alert-modal-message"
              className="custom-alert-modal-message"
            >
              {ctx.message}
            </div>
          </div>

          <div className="custom-alert-modal-actions">
            <button
              type="button"
              className="custom-alert-cancel-button"
              onClick={ctx.onClose}
              disabled={ctx.confirming}
            >
              Cancel
            </button>
            <button
              ref={okButtonRef}
              type="button"
              className={`custom-alert-confirm-button ${statusClass}`}
              onClick={ctx.onConfirm}
              disabled={ctx.confirming}
            >
              {ctx.confirming ? "Working..." : "OK"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`custom-alert-toast ${statusClass}`}
      role="alert"
      aria-live="assertive"
    >
      <div className="custom-alert-toast-icon">
        <FontAwesomeIcon icon={icon} />
      </div>

      <div className="custom-alert-toast-content">
        <div className="custom-alert-toast-message">{ctx.message}</div>
        <div className="custom-alert-toast-progress">
          <div className="custom-alert-toast-progress-bar" />
        </div>
      </div>

      <button
        type="button"
        className="custom-alert-toast-close"
        onClick={ctx.onClose}
        aria-label="Close notification"
      >
        <FontAwesomeIcon icon={faXmark} />
      </button>
    </div>
  );
}
